import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { captureUtmParams } from "@/lib/utm";
import { captureRefFromUrl } from "@/lib/partnerReferral";

// Capture UTM params + partner referral immediately on app load (before any routing)
captureUtmParams();
captureRefFromUrl().catch(() => {});
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { CookieConsent } from "@/components/CookieConsent";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";

// Eagerly load the landing page (critical path)
import Index from "./pages/Index";
import LocalizedIndex from "./pages/LocalizedIndex";

// ============================================================================
// CSRD-pivot (Fase 2a): HR-coaching routes er fjernet til fordel for
// AddonNordic CSRD for monday.com. Sider er fysisk arkiveret i Fase 2b.
// Kun core (auth/dashboard/settings/pricing), trust/compliance, admin og
// pain/* (under monitorering) routes beholdes.
// Soft rollback: git revert af denne fil.
// ============================================================================

// Core (auth + app shell)
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
// Bridge for e-conomic er arkiveret til _archived/bridge/ (juni 2026).
// Dette projekt er nu Time-only; Bridge bygges i et separat Lovable-projekt
// efter e-conomic app-partner-godkendelse.
// App-specific routes (REMIX: udskift med din nye apps routes-modul)
import { TimeAppRoutes } from "./app/time/routes";
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const CompanyContextPage = lazy(() => import("./pages/CompanyContext"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Org / team (genbruges som "organisation/seats" til CSRD)
const OrgAuditLog = lazy(() => import("./pages/OrgAuditLog"));
const TeamSeats = lazy(() => import("./pages/TeamSeats"));

// Compliance / trust (FROZEN — genbruges direkte til ESRS G1/governance)
const TrustCenter = lazy(() => import("./pages/TrustCenter"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const SciencePage = lazy(() => import("./pages/SciencePage"));
const EnterprisePage = lazy(() => import("./pages/EnterprisePage"));

// Billing
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Referral = lazy(() => import("./pages/Referral"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Pain pages — MONITORÉR (Fase 4 decision: behold som SEO-tragt eller fjern)
const PainPage = lazy(() => import("./pages/PainPage"));

// Admin (intern drift — beholdes uændret)
const AdminGrants = lazy(() => import("./pages/AdminGrants"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));
const AdminFunnel = lazy(() => import("./pages/AdminFunnel"));
const AdminAeoVisibility = lazy(() => import("./pages/AdminAeoVisibility"));
const AdminContent = lazy(() => import("./pages/AdminContent"));
const AdminKnowledge = lazy(() => import("./pages/AdminKnowledge"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminDaily = lazy(() => import("./pages/AdminDaily"));
const AdminRevenue = lazy(() => import("./pages/AdminRevenue"));
const AdminHealth = lazy(() => import("./pages/AdminHealth"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminPql = lazy(() => import("./pages/AdminPql"));
const AdminEmailAb = lazy(() => import("./pages/AdminEmailAb"));
const AdminBroLeads = lazy(() => import("./pages/AdminBroLeads"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const AdminDrafts = lazy(() => import("./pages/AdminDrafts"));
const AdminToday = lazy(() => import("./pages/AdminToday"));
const AdminPipeline = lazy(() => import("./pages/AdminPipeline"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminAiBias = lazy(() => import("./pages/AdminAiBias"));
const AdminAbuse = lazy(() => import("./pages/AdminAbuse"));
const AdminCompliance = lazy(() => import("./pages/AdminCompliance"));
const AdminValidationSprint = lazy(() => import("./pages/AdminValidationSprint"));
const AdminFounderHq = lazy(() => import("./pages/AdminFounderHq"));
const AdminIdeas = lazy(() => import("./pages/AdminIdeas"));
const AdminIdeaCompetitors = lazy(() => import("./pages/AdminIdeaCompetitors"));
const AdminScoreSimulator = lazy(() => import("./pages/AdminScoreSimulator"));
const AdminOperations = lazy(() => import("./pages/AdminOperations"));
const AdminRealWorld = lazy(() => import("./pages/AdminRealWorld"));

// Studio (foundation — remix-template admin)
const StudioLayout = lazy(() => import("./foundation/studio/StudioLayout"));
const StudioOverview = lazy(() => import("./foundation/studio/pages/Overview"));
const StudioRemix = lazy(() => import("./foundation/studio/pages/RemixSetup"));
const StudioDomain = lazy(() => import("./foundation/studio/pages/integrations/DomainCloudflare"));
const StudioMonday = lazy(() => import("./foundation/studio/pages/integrations/Monday"));
const StudioEconomic = lazy(() => import("./foundation/studio/pages/integrations/Economic"));
const StudioShopify = lazy(() => import("./foundation/studio/pages/integrations/Shopify"));
const StudioPaddle = lazy(() => import("./foundation/studio/pages/integrations/Paddle"));
const StudioSupport = lazy(() => import("./foundation/studio/pages/integrations/Support"));
const StudioMonitoring = lazy(() => import("./foundation/studio/pages/integrations/Monitoring"));
const StudioCore = lazy(() => import("./foundation/studio/pages/integrations/CoreApi"));
const StudioHealth = lazy(() => import("./foundation/studio/pages/Health"));
const StudioProfitability = lazy(() => import("./foundation/studio/pages/Profitability"));
const StudioAeo = lazy(() => import("./foundation/studio/pages/Aeo"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ErrorBoundary>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <Routes>
                {/* Landing */}
                <Route path="/" element={<Index />} />

                {/* App-specific routes (REMIX: udskift TimeAppRoutes) */}
                {TimeAppRoutes()}

                {/* Language-specific landing routes for SEO (hreflang targets) */}
                <Route path="/en" element={<LocalizedIndex lang="en" />} />
                <Route path="/no" element={<LocalizedIndex lang="nb" />} />
                <Route path="/sv" element={<LocalizedIndex lang="sv" />} />
                <Route path="/de" element={<LocalizedIndex lang="de" />} />
                <Route path="/fr" element={<LocalizedIndex lang="fr" />} />
                <Route path="/es" element={<LocalizedIndex lang="es" />} />
                <Route path="/pt" element={<LocalizedIndex lang="pt" />} />

                {/* Core app */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/csrd" element={<Navigate to="/" replace />} />
                <Route path="/csrd/*" element={<Navigate to="/" replace />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/security" element={<SecurityPage />} />
                <Route path="/settings/company-context" element={<CompanyContextPage />} />
                <Route path="/settings/audit" element={<OrgAuditLog />} />
                <Route path="/settings/team" element={<TeamSeats />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Compliance / trust (genbruges til ESRS G1) */}
                <Route path="/trust" element={<TrustCenter />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/science" element={<SciencePage />} />
                <Route path="/enterprise" element={<EnterprisePage />} />

                {/* Billing — generic (app-specific pricing routes ligger i app routes-modulet) */}
                <Route path="/pricing-csrd" element={<Navigate to="/" replace />} />
                <Route path="/refund" element={<RefundPolicy />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/checkout/success" element={<CheckoutReturn />} />
                <Route path="/referral" element={<Referral />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/email-unsubscribe" element={<Unsubscribe />} />

                {/* Admin (intern) */}
                <Route path="/admin" element={<AdminDaily />} />
                <Route path="/admin/grants" element={<AdminGrants />} />
                <Route path="/admin/feedback" element={<AdminFeedback />} />
                <Route path="/admin/funnel" element={<AdminFunnel />} />
                <Route path="/admin/aeo" element={<AdminAeoVisibility />} />
                <Route path="/admin/content" element={<AdminContent />} />
                <Route path="/admin/knowledge" element={<AdminKnowledge />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/revenue" element={<AdminRevenue />} />
                <Route path="/admin/health" element={<AdminHealth />} />
                <Route path="/admin/leads" element={<AdminLeads />} />
                <Route path="/admin/pql" element={<AdminPql />} />
                <Route path="/admin/email-ab" element={<AdminEmailAb />} />
                <Route path="/admin/bro-leads" element={<AdminBroLeads />} />
                <Route path="/admin/audit" element={<AdminAuditLog />} />
                <Route path="/admin/ai-bias" element={<AdminAiBias />} />
                <Route path="/admin/abuse" element={<AdminAbuse />} />
                <Route path="/admin/compliance" element={<AdminCompliance />} />
                <Route path="/admin/validation-sprint" element={<AdminValidationSprint />} />
                <Route path="/admin/founder-hq" element={<AdminFounderHq />} />
                <Route path="/admin/ideas" element={<AdminIdeas />} />
                <Route path="/admin/ideas/:id/competitors" element={<AdminIdeaCompetitors />} />
                <Route path="/admin/ideas/score-simulator" element={<AdminScoreSimulator />} />
                <Route path="/admin/operations" element={<AdminOperations />} />
                <Route path="/admin/real-world" element={<AdminRealWorld />} />
                <Route path="/admin/tasks" element={<AdminTasks />} />
                <Route path="/admin/drafts" element={<AdminDrafts />} />
                <Route path="/admin/today" element={<AdminToday />} />
                <Route path="/admin/pipeline" element={<AdminPipeline />} />

                {/* Studio (foundation — remix admin console) */}
                <Route path="/studio" element={<StudioLayout />}>
                  <Route index element={<StudioOverview />} />
                  <Route path="profitability" element={<StudioProfitability />} />
                  <Route path="remix" element={<StudioRemix />} />
                  <Route path="domain" element={<StudioDomain />} />
                  <Route path="monday" element={<StudioMonday />} />
                  <Route path="economic" element={<StudioEconomic />} />
                  <Route path="shopify" element={<StudioShopify />} />
                  <Route path="paddle" element={<StudioPaddle />} />
                  <Route path="support" element={<StudioSupport />} />
                  <Route path="aeo" element={<StudioAeo />} />
                  <Route path="monitoring" element={<StudioMonitoring />} />
                  <Route path="core" element={<StudioCore />} />
                  <Route path="health" element={<StudioHealth />} />
                </Route>

                {/* Pain SEO-pages (MONITORÉR — Fase 4 beslutning) */}
                <Route path="/:slug" element={<PainPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
